// ViewsPopupContents.jsx

import React, { useMemo } from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import { getDefaultViewTypeIconKey } from 'utilities/ViewTypeIcons';

// CSS
import './ViewsPopupContents.css';

// Components
import PopupOption from 'components/popup/option/PopupOption';
import PopupSeparator from 'components/popup/separator/PopupSeparator';

/**
 * ViewsPopupContents
 * 
 * Renders the popup component for the sidebar views.
 * 
 * @param {object[]} options - Views options to display.
 * @param {string|null} selectedViewId - Selected view id.
 * @param {function} onSelectOption - Handler for selecting an option.
 * @param {function} onEditOption - Handler for editing an option.
 *  
 *
 * @returns {ReactNode} - ViewsPopupContents.
 */
const ViewsPopupContents = React.memo(({
    options = [],
    selectedViewId = null,
    onSelectOption,
    onEditOption,
}) => {

    // ----- DEVICE DETECTION ----- //

    const isMobile = useIsMobile();

    // ----- MEMOS ----- //

    const viewOptions = useMemo(() => {
        return options
            .filter((option) => option.id !== '__add_view__');
    }, [options]);

    const canCreateView = useMemo(() => {
        return options.some((option) => option.id === '__add_view__');
    }, [options]);

    const canEditViews = typeof onEditOption === 'function';

    // ----- RENDER ----- //

    return (
        <div
          className="views-popup-contents-container"
          style={{
                maxWidth: isMobile ? '100%' : '400px',
            }}
        >

            {canCreateView ? (
                <>
                    <PopupOption
                      iconKey="add"
                      label="Create a Saved View"
                      onClick={() => onSelectOption?.('__add_view__')}
                    />

                    <PopupSeparator />
                </>
            ) : null}

            {viewOptions.map((option) => {

                const iconKey = getDefaultViewTypeIconKey(option?.type);
                const isSelected = option.id === selectedViewId;

                /**
                 * handleEditClick
                 *
                 * Handles edit click.
                 *
                 * @param {object} event - event.
                 */
                const handleEditClick = (event) => {
                    event.stopPropagation();
                    onEditOption?.(option.id);
                };

                return (
                    <PopupOption
                      key={option.id}
                      iconKey={iconKey}
                      label={option.label}
                      note={option.note || undefined}
                      settingsIconKey={canEditViews ? 'settings' : undefined}
                      selected={isSelected}
                      onClick={() => onSelectOption?.(option.id)}
                      onSettingsClick={canEditViews ? handleEditClick : undefined}
                    />
                );
            })}

        </div>
    );

});

// Dev Tools
ViewsPopupContents.displayName = 'ViewsPopupContents';

export default ViewsPopupContents;
